'use client';

import { useState } from 'react';

const cityTaxRates: { [key: string]: number } = {
  'new york': 8.875,
  'los angeles': 9.5,
  'chicago': 10.25,
  'houston': 8.25,
  'phoenix': 8.6,
  'philadelphia': 8.0,
  'san antonio': 8.25,
  'san diego': 7.75,
  'dallas': 8.25,
  'san jose': 9.375,
  'austin': 8.25,
  'seattle': 10.25,
  'denver': 8.81,
  'boston': 6.25,
  'miami': 7.0,
  'atlanta': 8.9,
  'detroit': 6.0,
  'portland': 0.0,
  'las vegas': 8.375,
  'nashville': 9.25,
};

const INITIAL_STATE = {
  amount: 100,
  city: '',
  taxRate: 7.5,
};

const PRESETS = {
  conservative: {
    amount: 50,
    city: 'boston',
    taxRate: 6.25,
  },
  moderate: {
    amount: 100,
    city: 'new york',
    taxRate: 8.875,
  },
  aggressive: {
    amount: 200,
    city: 'chicago',
    taxRate: 10.25,
  },
};

export default function CalculatorSalesTax() {
  const [amount, setAmount] = useState(INITIAL_STATE.amount);
  const [city, setCity] = useState(INITIAL_STATE.city);
  const [taxRate, setTaxRate] = useState(INITIAL_STATE.taxRate);
  const [isCalculating, setIsCalculating] = useState(false);
  const [cityFound, setCityFound] = useState<boolean | null>(null);

  const handleCityChange = (cityInput: string) => {
    setCity(cityInput);
    const normalizedCity = cityInput.toLowerCase().trim();
    
    if (cityTaxRates[normalizedCity] !== undefined) {
      setTaxRate(cityTaxRates[normalizedCity]);
      setCityFound(true);
      setIsCalculating(true);
      setTimeout(() => setIsCalculating(false), 800);
    } else if (cityInput.length > 0) {
      setCityFound(false);
    } else {
      setCityFound(null);
    }
  };

  const handleAmountChange = (value: number) => {
    setAmount(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const taxAmount = (amount * taxRate) / 100;
  const total = amount + taxAmount;

  const handleReset = () => {
    setAmount(INITIAL_STATE.amount);
    setCity(INITIAL_STATE.city);
    setTaxRate(INITIAL_STATE.taxRate);
    setCityFound(null);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setAmount(values.amount);
    setCity(values.city);
    setTaxRate(values.taxRate);
    setCityFound(true);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">City Name</label>
            <input
              type="text"
              value={city}
              onChange={(e) => handleCityChange(e.target.value)}
              placeholder="Enter city name (e.g., New York, Los Angeles)"
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
            {cityFound === false && (
              <p className="text-sm text-orange-600 mt-2">City not found. Using default rate of {taxRate}%</p>
            )}
            {cityFound === true && (
              <p className="text-sm text-emerald-600 mt-2">Tax rate for {city}: {taxRate}%</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Purchase Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => handleAmountChange(Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Tax Rate</label>
            <div className="relative">
              <input
                type="number"
                value={taxRate}
                onChange={(e) => {
                  setTaxRate(Number(e.target.value));
                  handleAmountChange(amount);
                }}
                step={0.1}
                className="w-full pl-3 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Total with Tax</div>
            <div className="text-5xl font-bold">${total.toFixed(2)}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Tax Amount</div>
              <div className="text-xl font-bold text-slate-900">${taxAmount.toFixed(2)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Original Amount</div>
              <div className="text-xl font-bold text-slate-900">${amount.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Supported cities:</strong> New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, Dallas, San Jose, Austin, Seattle, Denver, Boston, Miami, Atlanta, Detroit, Portland, Las Vegas, Nashville
          </p>
        </div>
      </div>
    </div>
  );
}